import React, { useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import Dashboard from "./pages/Dashboard";
import POManagement from "./pages/POManagement";
import JobDetail from "./pages/JobDetail";
import Reports from "./pages/Reports";
import MfgUserManagement from "./pages/settings/UserManagement";
import MfgRoleManagement from "./pages/settings/RoleManagement";
import WorkflowList from "./pages/settings/WorkflowList";
import WorkflowBuilder from "./pages/settings/WorkflowBuilder";
import PartsMaster from "./pages/settings/PartsMaster";
import InventorySettings from "./pages/settings/InventorySettings";
import CustomerPortalLinks from "./pages/settings/CustomerPortal";
import AuditLogPage from "../shared/AuditLogPage";
import InventoryList from "./pages/inventory/InventoryList";
import StockMovements from "./pages/inventory/StockMovements";
import CreatePO from "./pages/CreatePO";
import MyTasks from "./pages/MyTasks";
import AllottedTasks from "./pages/allotted/AllottedTasks";
import AssetManagement from "./pages/assets/AssetManagement";
import MachineDetail from "./pages/assets/MachineDetail";
import AssetSettings from "./pages/settings/AssetSettings";
import AlertRecipients from "./pages/settings/AlertRecipients";

import LoginPage from "../shared/LoginPage";
import ForgotPasswordPage from "../shared/ForgotPasswordPage";
import SetPasswordPage from "../shared/SetPasswordPage";
import ChangePasswordPage from "../shared/ChangePasswordPage";
import SubscriptionPage from "../shared/SubscriptionPage";

import AppShell from "../../components/layout/shell/AppShell";
import { MANUFACTURING_SECTIONS, MANUFACTURING_ADMIN } from "./manufacturingNavigation";
import ProtectedRoute from "../../components/layout/ProtectedRoute";
import MustChangePasswordGuard from "../../components/layout/MustChangePasswordGuard";
import ManufacturingRoute from "./ManufacturingRoute";
import useAuth from "../../hooks/useAuth";
import api from "../../api/client";
import { PERMISSIONS } from "../../constants/permissions";

function NavLayout() {
  const { user } = useAuth();
  const [requesting, setRequesting] = useState(false);

  const canManage = user?.isAdmin || user?.permissions?.includes(PERMISSIONS.MANUFACTURING_MANAGE);

  // Asks every worker with an active stage for a progress update.
  const onRequestUpdates = async () => {
    setRequesting(true);
    try {
      await api.post("/manufacturing/stages/request-updates");
    } finally {
      setRequesting(false);
    }
  };

  return (
    <AppShell
      storageKey="df.mfg.sidebarCollapsed"
      subtitle="Manufacturing & Production"
      homePath="/manufacturing/dashboard"
      sections={MANUFACTURING_SECTIONS}
      admin={MANUFACTURING_ADMIN}
      menuItems={[
        {
          label: requesting ? "Sending…" : "Request Updates",
          onClick: onRequestUpdates,
          disabled: requesting,
          hidden: !canManage,
        },
      ]}
    >
      <Routes>
        <Route index element={<Navigate to="/manufacturing/dashboard" replace />} />
        <Route path="/" element={<Navigate to="/manufacturing/dashboard" replace />} />
        <Route path="/change-password" element={<ChangePasswordPage />} />
        <Route path="/subscription" element={<SubscriptionPage />} />
        <Route path="/manufacturing/dashboard" element={<Dashboard />} />
        <Route path="/manufacturing/po-management" element={<POManagement />} />
        <Route path="/manufacturing/po/new" element={<CreatePO />} />
        <Route path="/manufacturing/po/:id" element={<JobDetail />} />
        <Route path="/manufacturing/my-tasks" element={<MyTasks />} />
        <Route path="/manufacturing/assigned-tasks" element={<AllottedTasks />} />
        <Route path="/manufacturing/assets" element={<AssetManagement />} />
        <Route path="/manufacturing/assets/machines/:id" element={<MachineDetail />} />
        <Route path="/manufacturing/reports" element={<Reports />} />
        <Route path="/manufacturing/settings/users" element={<MfgUserManagement />} />
        <Route path="/manufacturing/settings/roles" element={<MfgRoleManagement />} />
        <Route path="/manufacturing/settings/workflows" element={<WorkflowList />} />
        <Route path="/manufacturing/settings/workflows/new" element={<WorkflowBuilder />} />
        <Route path="/manufacturing/settings/workflows/:id" element={<WorkflowBuilder />} />
        <Route path="/manufacturing/settings/parts" element={<PartsMaster />} />
        <Route path="/manufacturing/settings/inventory" element={<InventorySettings />} />
        <Route path="/manufacturing/settings/customer-links" element={<CustomerPortalLinks />} />
        <Route path="/manufacturing/settings/assets" element={<AssetSettings />} />
        {/* Admin-only, like the audit trail — the page and its API guard on isAdmin. */}
        <Route path="/manufacturing/settings/alert-recipients" element={<AlertRecipients />} />
        {/* Audit trail is admin-only by design — the page guards on isAdmin. */}
        <Route
          path="/manufacturing/settings/audit-logs"
          element={<AuditLogPage backTo="/manufacturing/dashboard" backLabel="Dashboard" />}
        />
        <Route path="/manufacturing/inventory" element={<InventoryList />} />
        <Route path="/manufacturing/inventory/movements" element={<StockMovements />} />
      </Routes>
    </AppShell>
  );
}

export default function ManufacturingApp() {
  return (
    <Routes>
      {/* public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/set-password" element={<SetPasswordPage />} />

      {/* protected */}
      <Route element={<ProtectedRoute />}>
        <Route element={<ManufacturingRoute />}>
          <Route element={<MustChangePasswordGuard />}>
            <Route path="/*" element={<NavLayout />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}
